<template>
  <div class="px-4 py-4 bg-gray">
    <h5 class="mb-4">{{ $t('consents.title') }}</h5>

    <Loader :show="loading" />

    <p v-if="error" class="text-danger fw-bold">{{ error }}</p>

    <MDBRow v-if="!loading && !error">
      <MDBCol col="12" md="4" class="mb-3">
              <ConsentList
                :consents="consents"
                :selected-consent-id="selectedConsentId"
                :read-consent-ids="readConsentIds"
                @select-consent="handleSelectConsent"
              />

        <div class="bg-white p-3 mt-2">
          <small class="text-gray">
                  {{ $t('consents.requiredConsentsCompleted', { completed: completedCount, total: totalRequiredCount }) }}
                </small>
          <div class="progress mt-2">
                  <div
              class="progress-bar bg-danger"
                    :style="{ width: progressPercentage + '%' }"
                  ></div>
                </div>
              </div>
            </MDBCol>

      <MDBCol col="12" md="8" class="mb-3">
              <ConsentDetails
                v-if="selectedConsent"
                :consent="selectedConsent"
                :is-read="isConsentRead(selectedConsent.id)"
                :question-answers="getQuestionAnswers(selectedConsent.id)"
                @read="handleConsentRead"
                @question-answer="handleQuestionAnswer"
              />
        <div v-else class="bg-white p-5 text-center">
          <p class="text-gray">{{ $t('consents.selectConsentToView') }}</p>
              </div>
            </MDBCol>
          </MDBRow>

    <div v-if="!loading && !error" class="consents-actions">
            <SaveButton
              block
              :disabled="!canSave"
              :loading="saving"
              :error="saveError"
              :hint="saveBlockedHint"
              @save="handleSave"
            />
          </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { MDBRow, MDBCol } from "mdb-vue-ui-kit";
import { MarketingConsentService } from "@/services/marketing-consent.service";
import { transformConsents } from "@/utils/consent.utils";
import ConsentList from "@/components/Consents/ConsentList.vue";
import ConsentDetails from "@/components/Consents/ConsentDetails.vue";
import SaveButton from "@/components/Consents/SaveButton.vue";
import Loader from "@/components/General/Loader/index.vue";

const router = useRouter();
const { t } = useI18n();

const consents = ref([]);
const loading = ref(true);
const error = ref(null);
const selectedConsentId = ref(null);
const readConsentIds = ref(new Set());
const questionAnswers = ref({});
const saving = ref(false);
const saveError = ref(null);

const selectedConsent = computed(() => {
  return consents.value.find((c) => c.id === selectedConsentId.value);
});

const requiredConsents = computed(() => {
  return consents.value.filter((c) => c.required);
});

const totalRequiredCount = computed(() => {
  return requiredConsents.value.length;
});

const completedCount = computed(() => {
  return requiredConsents.value.filter((consent) => {
    const requiredQuestions = (consent.questions || []).filter((q) => q.required);
    if (requiredQuestions.length === 0) return false;

    const consentAnswers = questionAnswers.value[consent.id] || {};

    return requiredQuestions.every((q) => {
      const local = consentAnswers[q.id];
      const isQServerAnswered = q.answer !== null && q.answer !== undefined;
      if (isQServerAnswered) {
        return local !== false; // not explicitly unchecked
      } else {
        return local === true; // explicitly checked by user
      }
    });
  }).length;
});

const progressPercentage = computed(() => {
  if (totalRequiredCount.value === 0) return 100;
  return (completedCount.value / totalRequiredCount.value) * 100;
});

const unreadConsentNames = computed(() => {
  return consents.value
    .filter((c) => {
      if (c.answered) return false; // already answered server-side, no need to re-read
      return !readConsentIds.value.has(c.id);
    })
    .map((c) => c.name || `Consent #${c.id}`);
});

const pendingConsentNames = computed(() => {
  return consents.value
    .filter((consent) => {
      const requiredQuestions = (consent.questions || []).filter((q) => q.required);
      if (requiredQuestions.length === 0) return false;

      const consentAnswers = questionAnswers.value[consent.id] || {};

      return requiredQuestions.some((q) => {
        const local = consentAnswers[q.id];
        const isQServerAnswered = q.answer !== null && q.answer !== undefined;

        if (isQServerAnswered) {
          return local === false; // explicitly unchecked by user
        } else {
          return local !== true; // not yet accepted
        }
      });
    })
    .map((c) => c.name || `Consent #${c.id}`);
});

const canSave = computed(
  () => unreadConsentNames.value.length === 0 && pendingConsentNames.value.length === 0
);

const saveBlockedHint = computed(() => {
  if (canSave.value) return null;
  if (unreadConsentNames.value.length > 0) {
    const names = unreadConsentNames.value.map((n) => `"${n}"`).join(", ");
    return t('consents.readAllContentFirst', { names });
  }
  const names = pendingConsentNames.value.map((n) => `"${n}"`).join(", ");
  return t('consents.acceptAllRequiredIn', { names });
});

const isConsentRead = (consentId) => {
  return readConsentIds.value.has(consentId);
};

const handleSelectConsent = (consentId) => {
  selectedConsentId.value = consentId;
  if (!selectedConsentId.value && consents.value.length > 0) {
    selectedConsentId.value = consents.value[0].id;
  }
};

const handleConsentRead = (consentId) => {
  readConsentIds.value.add(consentId);
};

const handleQuestionAnswer = ({ consentId, questionId, answered }) => {
  if (!questionAnswers.value[consentId]) {
    questionAnswers.value[consentId] = {};
  }
  questionAnswers.value[consentId][questionId] = answered;
};

const getQuestionAnswers = (consentId) => {
  return questionAnswers.value[consentId] || {};
};

const handleSave = async () => {
  if (!canSave.value) {
    saveError.value =
      saveBlockedHint.value || t('consents.acceptAllBeforeSaving');
    return;
  }

  saving.value = true;
  saveError.value = null;

  try {
    const answers = [];

    consents.value.forEach((consent) => {
      if (!consent.questions || consent.questions.length === 0) return;

      const consentAnswers = questionAnswers.value[consent.id] || {};

      consent.questions.forEach((question) => {
        const local = consentAnswers[question.id];
        const isQServerAnswered = question.answer !== null && question.answer !== undefined;

        let accepted;
        if (isQServerAnswered) {
          accepted = local !== undefined ? local === true : true;
        } else {
          accepted = local === true;
        }

        if (question.required && !accepted) return;

        answers.push({
          question_id: question.id,
          accepted,
        });
      });
    });

    const response = await MarketingConsentService.submitConsents({ answers });

    const status = response.status ?? response.data?.status;
    const isSuccess =
      (status >= 200 && status < 300) ||
      response.data === null ||
      response.data === "" ||
      (response.data && response.data.success !== false);

    if (!isSuccess) {
      saveError.value = t("consents.failedToSave");
      return;
    }

    router.push("/dashboard");
  } catch (err) {
    console.error("Error saving consents:", err);
    saveError.value =
      err.response?.data?.message ||
      t('consents.errorSaving');
  } finally {
    saving.value = false;
  }
};

const fetchConsents = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await MarketingConsentService.getAllUserConsents();
    consents.value = transformConsents(response.data);

    consents.value.forEach((consent) => {
          questionAnswers.value[consent.id] = {};
      consent.questions.forEach((q) => {
        if (q.accepted !== undefined) {
          questionAnswers.value[consent.id][q.id] = q.accepted;
          }
        });
    });

    if (consents.value.length > 0 && !selectedConsentId.value) {
      selectedConsentId.value = consents.value[0].id;
    }
  } catch (err) {
    console.error("Error fetching consents:", err);
    error.value =
      err.response?.data?.message ||
      t('consents.failedToLoad');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchConsents();
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.consents-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #d9d9d9;
  text-align: center;
}

@media screen and (max-width: $mdBreakpoint) {
  .consents-actions {
    text-align: left;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
